import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

export function UpdatePriceDialog({ detail }: { detail: any }) {
  const [open, setOpen] = useState(false);
  const [profitMargin, setProfitMargin] = useState<number>(Number(detail.profitMargin) || 0);
  const [priceVND, setPriceVND] = useState<number>(Number(detail.priceVND) || 0);

  const costPrice = Number(detail.costPrice) || 0;
  const queryClient = useQueryClient();

  const hasPopulatedRef = useRef(false);

  useEffect(() => {
    if (open && !hasPopulatedRef.current) {
      hasPopulatedRef.current = true;
      setProfitMargin(Number(detail.profitMargin) || 0);
      setPriceVND(Number(detail.priceVND) || 0);
    }
    if (!open) {
      hasPopulatedRef.current = false;
    }
  }, [open, detail]);

  const handleMarginChange = (val: string) => {
    const margin = Number(val);
    setProfitMargin(margin);
    if (costPrice > 0) {
      const calculatedPrice = costPrice * (1 + margin / 100);
      setPriceVND(Math.round(calculatedPrice));
    }
  };

  const handlePriceChange = (val: string) => {
    const price = Number(val);
    setPriceVND(price);
    if (costPrice > 0) {
      const margin = ((price / costPrice) - 1) * 100;
      setProfitMargin(Number(margin.toFixed(2)));
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      return await trpcClient.quotations.updateDetailPrice.mutate({
        id: detail.id,
        profitMargin,
        priceVND,
      });
    },
    onSuccess: () => {
      toast.success("Cập nhật giá bán thành công");
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
      setOpen(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi cập nhật giá bán");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50">
          <Calculator className="w-4 h-4 mr-1" />
          Tính giá
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Cập nhật Giá Bán</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 py-4">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
            <Label>Giá Vốn (VND)</Label>
            <Input disabled value={new Intl.NumberFormat('vi-VN').format(costPrice)} className="bg-slate-50 font-medium" />
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
            <Label>% Lợi Nhuận (Margin)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={profitMargin} 
              onChange={(e) => handleMarginChange(e.target.value)}
              placeholder="Ví dụ: 15"
            />
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
            <Label>Giá Bán (VND)</Label>
            <Input 
              type="number" 
              value={priceVND} 
              onChange={(e) => handlePriceChange(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Hủy</Button>
          <Button onClick={() => mutate()} disabled={isPending || costPrice === 0}>
            {isPending ? "Đang lưu..." : "Lưu Thay Đổi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
